import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {List, Set} from 'immutable';
import classnames from 'classnames';
import isEmail from 'validator/lib/isEmail';
import track from 'react-tracking';
import errorCodes from 'client/errorCodes';

import withTracking from 'lib/hocs/withTracking';
import {doesUserBelongToAnyLicensedClassrooms} from 'lib/UserAccessUtil';
import ArrayToReadableFragment from 'lib/hocs/arrayToReadableFragment';
import {arrayToReadableString} from 'lib/stringUtils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import {Banner, Dialogue, LoadingSpinnerLong, TextAreaField} from '@albert-io/atomic';
import TrackedButton from 'client/components/Tracking/TrackedButton';
import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';

import {isManagedAccount} from 'client/Classrooms/shared/enrollUtils';

import {getModelForResourceType} from 'resources/modelRegistry';

import IBESummary from './IBEDialogues/IBESummary.react';
import IBEConfirmEmails from './IBEDialogues/IBEConfirmEmails.react';
import UploadCSVBtn from './UploadCSVBtn/UploadCSVBtn.react';
import EmailChips from './EmailChips/EmailChips.react';
import {bulkCreateClassroomInvitations} from './lib/bulkCreateClassroomInvitations';
import './invite-by-email.scss';

const delimiter = new RegExp(/[\n\r;, ]/, 'gi');

const TrackedTextAreaField = withTracking(TextAreaField, {
  onPaste: {content: (e) => e.clipboardData.getData('Text'), event: 'paste', type: null}
});

@track({component: 'InviteByEmail'})
class InviteByEmail extends React.Component {
  static propTypes = {
    classroomId: PropTypes.string,
    emailDomains: ImmutablePropTypes.listOf(getModelForResourceType('email_domain_v1')),
    handleClose: PropTypes.func.isRequired,
    inModal: PropTypes.bool,
    invitations: ImmutablePropTypes.listOf(getModelForResourceType('classroom_invitation_v1')),
    onSend: PropTypes.func,
    restrictClassroomEnrollment: PropTypes.bool,
    students: ImmutablePropTypes.listOf(getModelForResourceType('student_v2')),
    tracking: PropTypes.object,
    hardCap: PropTypes.bool,
    seatsRemaining: PropTypes.number
  };

  static defaultProps = {
    emailDomains: List(),
    invitations: List(),
    onSend: () => {},
    students: List()
  };

  constructor() {
    super();

    this.state = {
      emails: Set(),
      error: null,
      fulfilledInvitations: [],
      mustConfirmSubmission: false,
      rejectedInvitations: [],
      hardCapRejectedInvitations: [],
      requestInFlight: false,
      value: ''
    };
  }

  handleChange = (e) =>
    this.setState({
      error: null,
      value: e.target.value
    });

  handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.props.tracking.trackEvent({content: null, event: 'keyboard', type: 'enter'});
      this.handleSubmit();
    }
  };

  handleSubmit = () => {
    const input = this.state.value.trim().split(delimiter);
    const {restrictClassroomEnrollment} = this.props;
    const parsedInput = input
      .filter((item) => item) // filter out empty strings
      .reduce(
        (acc, curr) => {
          if (!isEmail(curr)) {
            acc.invalidItems.push({
              email: curr,
              reason: 'One or more of your entries is not a valid email.'
            });
            return acc;
          }
          if (
            restrictClassroomEnrollment &&
            !this.props.emailDomains.isEmpty() &&
            !isManagedAccount(curr, this.props.emailDomains)
          ) {
            const domainNames = this.props.emailDomains.map((domain) => domain.getName());
            acc.invalidItems.push({
              email: curr,
              reason: `Invitations are restricted to emails ending in ${arrayToReadableString(
                [...domainNames],
                'or'
              )}.`
            });
            return acc;
          }
          acc.validItems.push(curr);
          return acc;
        },
        {
          invalidItems: [],
          validItems: []
        }
      );

    if (parsedInput.invalidItems.length) {
      this.props.tracking.trackEvent({
        content: parsedInput.invalidItems[0].reason,
        event: 'error',
        type: null
      });
    }

    this.setState(({emails}) => ({
      emails: emails.union(parsedInput.validItems),
      error: parsedInput.invalidItems.length ? parsedInput.invalidItems[0].reason : null,
      value: parsedInput.invalidItems.map((item) => item.email).join(';\n')
    }));
  };

  handleCSVUpload = (emails) => {
    this.props.tracking.trackEvent({content: null, event: 'csv_upload', type: null});
    this.setState(({value}) => {
      const updatedValue = value
        .trim()
        .split(delimiter)
        .filter((item) => item) // Remove any empty strings among items
        .concat(emails)
        .join(';\n');
      return {value: updatedValue};
    }, this.handleSubmit);
  };

  handleSend = async () => {
    this.setState({
      requestInFlight: true
    });

    const invitations = this.state.emails
      .filterNot(this.doesInvitationAlreadyExist)
      .reduce((acc, email) => {
        acc.push({
          classroom_id: this.props.classroomId,
          email,
          issuer_id: masqueradeStore.getUserIdByMasqueradeState()
        });
        return acc;
      }, []);

    const parsedResponse = {
      fulfilled: [],
      rejected: [],
      hardCapRejected: []
    };

    try {
      const response = await bulkCreateClassroomInvitations(invitations);
      parsedResponse.fulfilled = response
        .toJS()
        .map((invitation) => invitation.email)
        .sort();
    } catch (reason) {
      if (
        reason?.response?.body?.errors?.find(
          (error) => error.code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT
        )
      ) {
        parsedResponse.hardCapRejected = this.state.emails;
      } else {
        parsedResponse.rejected = this.state.emails;
      }
    }

    this.setState(({emails}) => ({
      emails: emails.subtract(parsedResponse.fulfilled),
      fulfilledInvitations: parsedResponse.fulfilled,
      mustConfirmSubmission: false,
      rejectedInvitations: parsedResponse.rejected,
      hardCapRejectedInvitations: parsedResponse.hardCapRejected,
      requestInFlight: false
    }));

    if (parsedResponse.fulfilled.length > 0) {
      this.props.onSend();
    }
  };

  doesInvitationAlreadyExist = (email) => {
    return (
      this.props.students.some((student) => student.getEmail() === email) ||
      this.props.invitations.some((invitation) => invitation.getEmail() === email)
    );
  };

  removeEmail = (email) => {
    this.setState(({emails}) => ({emails: emails.delete(email)}));
  };

  render() {
    const {
      classroomId,
      emailDomains,
      seatsRemaining,
      handleClose,
      restrictClassroomEnrollment,
      hardCap,
      ...rest
    } = this.props;

    const {fulfilledInvitations, rejectedInvitations, requestInFlight, hardCapRejectedInvitations} =
      this.state;

    const showSummary =
      fulfilledInvitations.length !== 0 ||
      rejectedInvitations.length !== 0 ||
      hardCapRejectedInvitations.length !== 0;

    const {isReady: hasFetchedLicenses, value: hasLicense} = doesUserBelongToAnyLicensedClassrooms(
      true // Query teacher's licenses
    );

    if (!hasFetchedLicenses || requestInFlight) {
      return (
        <Dialogue hideCloseButton {...rest}>
          <Dialogue.Body>
            <LoadingSpinnerLong />
          </Dialogue.Body>
        </Dialogue>
      );
    }

    if (showSummary) {
      return (
        <IBESummary
          onBackBtnClick={() =>
            this.setState({
              fulfilledInvitations: [],
              mustConfirmSubmission: false,
              rejectedInvitations: [],
              hardCapRejectedInvitations: []
            })
          }
          fulfilledInvitations={fulfilledInvitations}
          hardCapRejectedInvitations={hardCapRejectedInvitations}
          handleClose={handleClose}
          rejectedInvitations={rejectedInvitations}
          {...rest}
        />
      );
    }

    if (this.state.mustConfirmSubmission) {
      const validEmails = this.state.emails.toList().filterNot(this.doesInvitationAlreadyExist);
      const invalidEmails = this.state.emails.toList().filter(this.doesInvitationAlreadyExist);

      return (
        <IBEConfirmEmails
          classroomId={classroomId}
          handleClose={handleClose}
          validEmails={validEmails}
          invalidEmails={invalidEmails}
          onBackBtnClick={() => this.setState({mustConfirmSubmission: false})}
          onConfirm={this.handleSend}
          {...rest}
        />
      );
    }

    const showBanner = restrictClassroomEnrollment && !emailDomains.isEmpty();

    return (
      <Dialogue
        handleClose={handleClose}
        {...rest}
        paddingLevel={0}
        size='m'
        className={classnames(rest.className, 'flex! max-h-full flex-col overflow-hidden!')}
      >
        <Dialogue.Title size='s' className='px-x2 pt-x1 shrink-0 grow-0!'>
          Invite Students by Email
        </Dialogue.Title>
        {hardCap && (
          <div className='shrink-0'>
            <HardCapWarningBanner seatsRemaining={seatsRemaining} />
          </div>
        )}
        <Dialogue.Body className='p-x3 min-h-x0 flex-1 overflow-y-auto'>
          <TrackedTextAreaField
            border
            value={this.state.value}
            className='mb-x2 max-w-[70ch]'
            error={Boolean(this.state.error)}
            label='Enter email address(es)'
            message={this.state.error}
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
            placeholder='Separate multiple email addressess using a comma, space, or semicolon'
            type='text'
            data-testid='invite-by-email-textarea'
          />
          <div className='flex items-center'>
            <TrackedButton className='mb-x2 ml-auto' onClick={this.handleSubmit}>
              Add
            </TrackedButton>
          </div>
          <EmailChips
            className='ibe-email-list mt-x2 mb-x2 overflow-y-auto'
            emails={this.state.emails}
            handleClick={this.removeEmail}
          />
          <UploadCSVBtn className='pt-x2 mr-auto' onUpload={this.handleCSVUpload} />
          {showBanner && (
            <Banner className='mt-x2'>
              <span>
                Invitations are restricted to accounts ending in{' '}
                <ArrayToReadableFragment
                  arr={[...emailDomains.map((domain) => domain.getName())]}
                  as='strong'
                />
                .
              </span>
            </Banner>
          )}
        </Dialogue.Body>
        <div className='mt-x3 mb-x2 p-x2 flex shrink-0 items-center'>
          <TrackedButton className='ml-auto' color='secondary' onClick={handleClose}>
            Cancel
          </TrackedButton>
          <TrackedButton
            className='ml-x1'
            disabled={this.state.emails.isEmpty()}
            onClick={() => this.setState({mustConfirmSubmission: true})}
          >
            Send Invitations
          </TrackedButton>
        </div>
      </Dialogue>
    );
  }
}

export default InviteByEmail;
